import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  PDF_MINIMAP_MARKER_HEIGHT_PX,
  PDF_MINIMAP_MARKER_INSET_PX,
  PDF_MINIMAP_WIDTH_PX,
} from '@/constants/pdf';
import { cn } from '@/lib/utils';
import type { SearchResult } from './types';

interface PdfMinimapProps {
  results: SearchResult[];
  totalPages: number;
  currentPage: number;
  currentResultIndex: number;
  onSelectResult: (index: number) => void;
}

/** Gutter showing where in the document the matches sit. */
const PdfMinimap: React.FC<PdfMinimapProps> = ({
  results,
  totalPages,
  currentPage,
  currentResultIndex,
  onSelectResult,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="relative shrink-0 border-l border-black/30 bg-black/20"
      style={{ width: PDF_MINIMAP_WIDTH_PX }}
    >
      <div
        className="pointer-events-none absolute inset-x-0 border-y border-[rgba(100,149,237,0.8)] bg-[rgba(100,149,237,0.3)]"
        style={{
          top: `${((currentPage - 1) / totalPages) * 100}%`,
          height: `${(1 / totalPages) * 100}%`,
        }}
      />
      <TooltipProvider delayDuration={400}>
        {results.map((result, index) => {
          const label = t('documents.pageResultPreview', {
            page: result.page,
            text: result.text.substring(0, 30),
          });

          return (
            <Tooltip key={index}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={label}
                  onClick={() => onSelectResult(index)}
                  // Out of the tab order: these were unfocusable divs before, and
                  // a long document would otherwise insert one stop per match in
                  // a 24px gutter. The results list navigates the same matches.
                  tabIndex={-1}
                  className={cn(
                    'absolute rounded-[4px] transition-all duration-150 hover:scale-y-150 hover:bg-[rgb(255,165,0)]',
                    // Same orange as the match rectangle painted on the page.
                    index === currentResultIndex
                      ? 'bg-[rgba(255,165,0,0.9)]'
                      : 'bg-[rgba(255,255,0,0.8)]',
                  )}
                  style={{
                    top: `${((result.page - 1) / totalPages) * 100}%`,
                    left: PDF_MINIMAP_MARKER_INSET_PX,
                    right: PDF_MINIMAP_MARKER_INSET_PX,
                    height: PDF_MINIMAP_MARKER_HEIGHT_PX,
                  }}
                />
              </TooltipTrigger>
              <TooltipContent side="left" className="revy-ui">
                {label}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </TooltipProvider>
    </div>
  );
};

export default PdfMinimap;
